/**
 * Helper functions for creating and managing organization entitlements
 *
 * SINGLE SOURCE OF TRUTH for plan limits (PLAN_LIMITS) and the plan feature
 * matrix (PLAN_FEATURE_MATRIX). This module is a dependency leaf (no imports)
 * so it is safe to import from both client and server code without circular
 * imports. Satellite modules (billing/plan-entitlements.ts,
 * utils/plan-features.ts, config/plans.ts) derive their numbers from here —
 * do not hardcode plan limits anywhere else.
 */

type PlanTier = 'starter' | 'professional' | 'enterprise';

/**
 * Sentinel value for unlimited valuations (Enterprise plan).
 * The database column `max_valuations` is INTEGER NOT NULL, so unlimited is
 * represented as -1. Always use isUnlimitedValuations() to check.
 */
export const UNLIMITED_VALUATIONS = -1;

/**
 * Check whether a max_valuations value means "unlimited" (Enterprise).
 */
export function isUnlimitedValuations(maxValuations: number): boolean {
  return maxValuations < 0;
}

/**
 * Canonical numeric limits per plan.
 * Matches the public pricing page: Starter = monthly (12/year),
 * Professional = weekly (52/year), Enterprise = unlimited (-1 sentinel).
 */
export const PLAN_LIMITS: Record<
  'demo' | PlanTier,
  { maxValuations: number; maxOrganizations: number; maxUsers: number }
> = {
  demo: { maxValuations: 0, maxOrganizations: 0, maxUsers: 0 },
  starter: { maxValuations: 12, maxOrganizations: 1, maxUsers: 1 },
  professional: { maxValuations: 52, maxOrganizations: 1, maxUsers: 3 },
  enterprise: { maxValuations: UNLIMITED_VALUATIONS, maxOrganizations: 1, maxUsers: 9 },
};

export interface PlanFeatureMatrixEntry {
  features: {
    playerSalesTracking: boolean;
    sponsorshipSponsors: boolean;
    sponsorshipPackages: boolean;
    sponsorshipInventory: boolean;
    sponsorshipProposals: boolean;
    sponsorshipContractApproval: boolean;
    restApi: boolean;
    auditLog: boolean;
    documents: boolean;
    dashboard: boolean;
    valuationTracking: boolean;
    methods: boolean;
    settings: boolean;
  };
  reports: {
    basicPdf: boolean;
    brandedPdf: boolean;
    analytics: boolean;
  };
  supportLevel: 'email' | 'priority' | 'dedicated';
  ssoEnabled: boolean;
}

/**
 * Canonical feature matrix for PAID plans.
 * Used by onboarding (createEntitlementsFromOnboarding) and plan changes
 * (utils/plan-features.ts) so the two can never drift.
 *
 * NOTE: plugins (aiInsights/sankeyChart) are intentionally NOT in this matrix —
 * they are add-on-dependent and each consumer applies its own add-on logic.
 * Demo is also not here: demo entitlements (all-true view-only, gated by
 * isDemo) live in billing/plan-entitlements.ts, while the demo row in
 * utils/plan-features.ts is a marketing/compare matrix (all-false).
 */
export const PLAN_FEATURE_MATRIX: Record<PlanTier, PlanFeatureMatrixEntry> = {
  starter: {
    features: {
      playerSalesTracking: false,
      sponsorshipSponsors: false,
      sponsorshipPackages: false,
      sponsorshipInventory: false,
      sponsorshipProposals: false,
      sponsorshipContractApproval: false,
      restApi: false,
      auditLog: false,
      documents: false,
      dashboard: true,
      valuationTracking: true,
      methods: true,
      settings: true,
    },
    reports: {
      basicPdf: true,
      brandedPdf: false,
      analytics: false,
    },
    supportLevel: 'email',
    ssoEnabled: false,
  },
  professional: {
    features: {
      playerSalesTracking: true,
      sponsorshipSponsors: true, // Can create/edit sponsors
      sponsorshipPackages: false, // No Package Builder
      sponsorshipInventory: false,
      sponsorshipProposals: false,
      sponsorshipContractApproval: false,
      restApi: false,
      auditLog: false,
      documents: true,
      dashboard: true,
      valuationTracking: true,
      methods: true,
      settings: true,
    },
    reports: {
      basicPdf: true,
      brandedPdf: true,
      analytics: true,
    },
    supportLevel: 'priority',
    ssoEnabled: false,
  },
  enterprise: {
    features: {
      playerSalesTracking: true,
      sponsorshipSponsors: true,
      sponsorshipPackages: true, // Full Package Builder access
      sponsorshipInventory: true,
      sponsorshipProposals: true,
      sponsorshipContractApproval: true,
      restApi: true,
      auditLog: true,
      documents: true,
      dashboard: true,
      valuationTracking: true,
      methods: true,
      settings: true,
    },
    reports: {
      basicPdf: true,
      brandedPdf: true,
      analytics: true,
    },
    supportLevel: 'dedicated',
    ssoEnabled: true,
  },
};

interface EntitlementsInsert {
  organization_id: string;
  plan: PlanTier;
  billing_period: 'monthly' | 'yearly'; // Required — all rows populated (verified dev+prod 2026-10-07)
  subscription_period_start: string; // ISO timestamp when current subscription period started
  purchased_valuations: number; // Extra valuations purchased (one-time)
  is_demo: boolean;
  max_valuations: number;
  max_organizations: number;
  max_users: number;
  sso_enabled: boolean; // Single Sign-On (Enterprise only)
  features: Record<string, boolean>;
  reports: Record<string, boolean>;
  plugins: Record<string, boolean>;
  support_level: string;
}

/**
 * Create organization_entitlements record from onboarding data
 */
export function createEntitlementsFromOnboarding(
  organizationId: string,
  subscriptionPlan: string,
  subscriptionPeriod: string,
  subscriptionAddons: string[]
): EntitlementsInsert {
  const plan = subscriptionPlan.toLowerCase() as PlanTier;

  // Canonical limits and features (see PLAN_LIMITS / PLAN_FEATURE_MATRIX above)
  const planLimits = PLAN_LIMITS[plan] || PLAN_LIMITS.starter;
  const planMatrix = PLAN_FEATURE_MATRIX[plan] || PLAN_FEATURE_MATRIX.starter;

  // Copy so callers can never mutate the shared matrix
  const features = { ...planMatrix.features };
  const reports = { ...planMatrix.reports };

  // Plugins based on plan and addons
  const hasAIInsights = plan === 'enterprise' || subscriptionAddons.includes('ai-insights');

  const hasSankeyChart =
    plan === 'enterprise' ||
    subscriptionAddons.includes('sankey-chart') ||
    subscriptionAddons.includes('sankey'); // LIVE alias — config/plans.ts AddOnId and billing page still send 'sankey' (not a removable shim; verified 2026-10-07)

  const plugins = {
    marketplace: true,
    aiInsights: hasAIInsights,
    sankeyChart: hasSankeyChart,
  };

  return {
    organization_id: organizationId,
    plan,
    billing_period: subscriptionPeriod.toLowerCase() as 'monthly' | 'yearly',
    subscription_period_start: new Date().toISOString(), // Start of subscription period (now)
    purchased_valuations: 0, // No purchased valuations initially
    is_demo: false,
    max_valuations: planLimits.maxValuations,
    max_organizations: planLimits.maxOrganizations,
    max_users: planLimits.maxUsers,
    sso_enabled: planMatrix.ssoEnabled, // SSO only for Enterprise plan
    features,
    reports,
    plugins,
    support_level: planMatrix.supportLevel,
  };
}

/**
 * Update existing entitlements (for plan upgrades/downgrades)
 */
export function updateEntitlementsFromPlan(
  subscriptionPlan: string,
  subscriptionPeriod: string,
  subscriptionAddons: string[]
): Partial<EntitlementsInsert> {
  // Use same logic as create, but return only fields that should be updated
  const entitlements = createEntitlementsFromOnboarding(
    '', // organization_id not needed for update
    subscriptionPlan,
    subscriptionPeriod,
    subscriptionAddons
  );

  // Return everything except fields that must never be reset by a sync:
  // - organization_id / is_demo: identity, not plan-derived
  // - purchased_valuations: one-time purchases are valid forever — including
  //   the create-path's 0 here zeroed them on every subscription.updated
  //   webhook (fixed 2026-10-08)
  // - subscription_period_start: the cumulative valuation limit's growth
  //   clock (feature-gate) — including the create-path's "now" here restarted
  //   it on every webhook; syncEntitlementsFromStripe sets it explicitly on
  //   genuine renewals only (fixed 2026-10-08)
  const updateFields: Partial<EntitlementsInsert> = { ...entitlements };
  delete updateFields.organization_id;
  delete updateFields.is_demo;
  delete updateFields.purchased_valuations;
  delete updateFields.subscription_period_start;

  return updateFields;
}
